import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { collection, getDocs, query, where } from "firebase/firestore";
import { paths, type CallLog, type ServiceRequest } from "@clevis/shared-types";
import { Card, CardTitle } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { useLeadSources } from "../hooks/useLeadSources";

// Reports → Lead sources: for the period, each source's calls (tracking
// numbers), website requests, new customers, and the revenue invoiced to
// customers who came from it. The sources and the numbers are the company's
// (Setup → Lead sources); this only adds up.

const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

interface Row {
  source: string;
  calls: number;
  missed: number;
  requests: number;
  customers: number;
  revenueCents: number;
}

export function LeadSourcesCard({
  tenantId,
  invoices,
  start,
  end,
  rangeLabel,
}: {
  tenantId: string;
  invoices: Array<{ customerId: string | null; totalCents: number; status: string; createdAt: number }>;
  start: number;
  end: number;
  rangeLabel: string;
}) {
  const sources = useLeadSources(tenantId);
  const [calls, setCalls] = useState<CallLog[]>([]);
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [customers, setCustomers] = useState<Array<{ id: string; leadSource: string; createdAt: number }>>([]);

  useEffect(() => {
    let dead = false;
    void (async () => {
      const [c, r, cu] = await Promise.all([
        getDocs(query(collection(db, paths.tenantCalls(tenantId)), where("startedAt", ">=", start), where("startedAt", "<", end))).catch(() => null),
        getDocs(query(collection(db, paths.tenantRequests(tenantId)), where("createdAt", ">=", start), where("createdAt", "<", end))).catch(() => null),
        getDocs(query(collection(db, paths.tenantCustomers(tenantId)), where("leadSource", "!=", ""))).catch(() => null),
      ]);
      if (dead) return;
      setCalls(c?.docs.map((d) => d.data() as CallLog) ?? []);
      setRequests(r?.docs.map((d) => d.data() as ServiceRequest) ?? []);
      setCustomers(cu?.docs.map((d) => ({ id: d.id, leadSource: (d.get("leadSource") as string) ?? "", createdAt: (d.get("createdAt") as number) ?? 0 })) ?? []);
    })();
    return () => {
      dead = true;
    };
  }, [tenantId, start, end]);

  const rows = useMemo(() => {
    const by = new Map<string, Row>();
    const row = (s: string) => by.get(s) ?? by.set(s, { source: s, calls: 0, missed: 0, requests: 0, customers: 0, revenueCents: 0 }).get(s)!;
    for (const s of sources) row(s);
    for (const c of calls) {
      const r = row(c.source);
      r.calls += 1;
      if (c.status === "missed" || c.status === "busy" || c.status === "failed") r.missed += 1;
    }
    for (const q of requests) if (q.lead?.howHeard) row(q.lead.howHeard).requests += 1;
    const sourceOf = new Map(customers.map((c) => [c.id, c.leadSource]));
    for (const c of customers) if (c.createdAt >= start && c.createdAt < end) row(c.leadSource).customers += 1;
    for (const inv of invoices) {
      if (inv.status === "draft" || inv.status === "void" || inv.createdAt < start || inv.createdAt >= end) continue;
      const s = inv.customerId ? sourceOf.get(inv.customerId) : undefined;
      if (s) row(s).revenueCents += inv.totalCents;
    }
    return [...by.values()].sort((a, b) => b.revenueCents - a.revenueCents || b.calls + b.requests - (a.calls + a.requests) || a.source.localeCompare(b.source));
  }, [sources, calls, requests, customers, invoices, start, end]);

  const any = rows.some((r) => r.calls || r.requests || r.customers || r.revenueCents);
  return (
    <Card>
      <div className="mb-1 flex items-center justify-between gap-2">
        <CardTitle className="mb-0">Lead sources · {rangeLabel}</CardTitle>
        <Link href="/leads" className="text-xs text-steel hover:text-ink">
          Set up sources &amp; numbers
        </Link>
      </div>
      {!any ? (
        <p className="text-xs text-steel">
          Nothing to add up yet. Lead sources come from calls to your tracking numbers, the website form's "how did you hear", and the
          Lead source on each customer.
        </p>
      ) : (
        <div className="text-xs">
          <div className="grid grid-cols-[minmax(0,1fr)_repeat(4,4.5rem)] gap-2 border-b border-steel/15 pb-1 text-steel">
            <span>Source</span>
            <span className="text-right">Calls</span>
            <span className="text-right">Requests</span>
            <span className="text-right">New cust.</span>
            <span className="text-right">Invoiced</span>
          </div>
          {rows.map((r) => (
            <div key={r.source} className="grid grid-cols-[minmax(0,1fr)_repeat(4,4.5rem)] gap-2 py-1 text-ink">
              <span className="truncate">{r.source}</span>
              <span className="text-right">
                {r.calls}
                {r.missed ? <span className="text-danger"> ({r.missed} missed)</span> : null}
              </span>
              <span className="text-right">{r.requests}</span>
              <span className="text-right">{r.customers}</span>
              <span className="text-right font-semibold">{r.revenueCents ? money(r.revenueCents) : "–"}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
